import React, { useCallback } from 'react';
import { BoardInsightsButton } from '@atlassian/jira-insights-board-button/src/ui/index.tsx';
import { KanbanBoardInsightsButton } from '@atlassian/jira-insights-board-button/src/ui/kanban-button/index.tsx';
import { useIsCurrentBrowserSupported } from '@atlassian/jira-insights-common/src/controllers/use-is-current-browser-supported/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { ServicedeskBoardInsightsButton } from '@atlassian/jira-servicedesk-board-button/src/ui/index.tsx';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { Capability } from '../../../common/capability/index.tsx';
import { INSIGHTS_PANEL } from '../../../model/panels/constants.tsx';
import type { PanelType } from '../../../model/panels/types.tsx';
import {
	closeViewSettingsPanel,
	type TogglePanelAction,
} from '../../../state/actions/panels/index.tsx';
import { useBoardActionCreator } from '../../../state/index.tsx';
import { useIsCMPBoard, useCapability } from '../../../state/state-hooks/capabilities/index.tsx';

type Props = {
	isInsightsPanelOpen: boolean;
	boardId: number;
	edition: ApplicationEdition | null;
	onToggleInsightsPanel: (arg1: PanelType) => TogglePanelAction;
	isSprintsEnabled: boolean;
	isHeaderCompactMode?: boolean;
};

const InsightsButtonCommon = (props: Props) => {
	const isSupported = useIsCurrentBrowserSupported();
	const isVisibleForSoftware = useCapability(Capability.RENDER_BOARD_INSIGHT_BUTTON);
	const isVisibleForServiceDesk = useCapability(Capability.RENDER_JSM_BOARD_INSIGHT_BUTTON);
	const { data: project } = useProjectContext();
	const { isSoftwareFullTheming } = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	const closeViewSettingsPanelAction = useBoardActionCreator(() => closeViewSettingsPanel());

	const {
		isInsightsPanelOpen,
		onToggleInsightsPanel,
		boardId,
		edition,
		isSprintsEnabled,
		isHeaderCompactMode,
	} = props;

	const isCMPBoard = useIsCMPBoard();
	const onToggleInsightsPanelHandler = useCallback(() => {
		onToggleInsightsPanel(INSIGHTS_PANEL);
		closeViewSettingsPanelAction();
	}, [closeViewSettingsPanelAction, onToggleInsightsPanel]);

	if (!isSupported) {
		return null;
	}

	// this is only because of flow, it will never happen
	if (!edition) {
		return null;
	}

	if (isVisibleForServiceDesk) {
		return (
			<ServicedeskBoardInsightsButton
				isSimplifiedProject={!isCMPBoard}
				isOpen={isInsightsPanelOpen}
				boardId={Number(boardId)}
				edition={edition}
				projectId={project?.projectId}
				onToggle={onToggleInsightsPanelHandler}
				{...(getIsSoftwareThemingEnabled() && {
					isThemed: isSoftwareFullTheming,
				})}
			/>
		);
	}

	if (isVisibleForSoftware) {
		if (isSprintsEnabled) {
			return (
				<BoardInsightsButton
					isSimplifiedProject={!isCMPBoard}
					isOpen={isInsightsPanelOpen}
					isCompact={isHeaderCompactMode}
					boardId={Number(boardId)}
					edition={edition}
					onToggle={onToggleInsightsPanelHandler}
					{...(getIsSoftwareThemingEnabled() && {
						isThemed: isSoftwareFullTheming,
					})}
				/>
			);
		}
		return (
			<KanbanBoardInsightsButton
				isSimplifiedProject={!isCMPBoard}
				isOpen={isInsightsPanelOpen}
				isCompact={isHeaderCompactMode}
				boardId={Number(boardId)}
				edition={edition}
				onToggle={onToggleInsightsPanelHandler}
				{...(getIsSoftwareThemingEnabled() && {
					isThemed: isSoftwareFullTheming,
				})}
			/>
		);
	}

	return null;
};

export const InsightsButton = InsightsButtonCommon;
